import React, { useCallback, useEffect, useState } from 'react';
import { useProject } from '../ProjectContext';
import { getProjectId, getProjectName } from '@shared/targetHelpers';
import {
  requestReportDownload,
  fetchReportDownloadStatus,
  requestPdfReportDownload,
  fetchPdfReportDownloadStatus,
  requestDrawingRegister,
  requestDrawingComparison,
  waitForReport,
} from '../services/api';
import { Button, Card, PillGroup, Loading, ErrorText, errorMessage } from '../components/ui';
import { fetchSavedAnswers, fetchSavedAnswerRecord, downloadBlob, exportChatAnswer, buildSavedReport, fetchSavedReports } from '../services/api';
import { AnswerBody } from './ChatPage';
import ShareSavedFile from '../components/ShareSavedFile';
import VerifySavedFile from '../components/VerifySavedFile';
import ProcoreSendToDocumentsCard from '../components/ProcoreSendToDocuments';
import { formatAnswer } from '@shared/formatAnswer';

const PDF_REPORT_KINDS = [
  { id: 'project_report', label: 'Project report' },
  { id: 'project_weekly', label: 'Weekly lookahead' },
  { id: 'photo_report', label: 'Photo evidence' },
];

// The finished file comes back as a short-lived SAS URL whose
// Content-Disposition is already `attachment`, so following it downloads
// rather than navigating away.
//
// The click is attempted automatically, but the link is ALSO rendered for the
// user to press. By the time a report finishes, minutes may have passed since
// the button was clicked, so the browser no longer treats this as
// user-initiated and blocks it silently - which is exactly why the previous
// window.open() inside a polling callback appeared to do nothing at all.
function startDownload(url, fileName) {
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'report';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch { /* the rendered link below is the reliable path */ }
}

// Chat answers somebody chose to keep.
//
// They live here rather than in Documents because they are this app's output,
// not the customer's source material - the same reason the drawing register
// is a report rather than a document. They are stored under the app's own
// underscore prefix, which is what keeps them out of the library and out of
// the search index: an answer must never become a source that a later answer
// cites back as evidence.
const str = (v) => (v == null ? '' : String(v));

// "This one is in the project record, not just on somebody's laptop."
//
// The same badge the documents list uses, for the same reason and read from the
// same stamp: a push writes it onto whatever blob it sent, so a saved answer or
// a built report that went to Procore has always carried the evidence - the
// Reports list simply never showed it, and a conclusion filed as the project's
// record looked identical to one that had only ever been downloaded.
//
// Only the sent direction exists here. These are this app's own output, so
// there is no "from Procore" case to show: nothing arrives as a saved answer.
// "A person has signed this off", which is a different claim from "this was
// produced" - and the one that decides whether anybody outside should act on
// it. Drawn from the stamp the verify route writes onto the file, so a list of
// fifty answers does not read fifty verification records to badge them.
function VerifiedBadge({ verifiedBy, verifiedAt }) {
  if (!verifiedBy) return null;
  const when = verifiedAt
    ? new Date(verifiedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '';
  return (
    <span className="badge-verified" title={`Verified by ${verifiedBy}${when ? ` on ${when}` : ''}`}>
      Verified
    </span>
  );
}

function ProcoreSentBadge({ procore }) {
  if (!procore?.pushedAt) return null;
  const when = new Date(procore.pushedAt).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
  return (
    <span className="badge-sent" title={`Sent to Procore ${when}`}>
      To Procore
    </span>
  );
}

function SavedAnswers({ projectId, projectName, project, onBuilt }) {
  // Which answer has its share or Procore panel open. One at a time, by blob
  // name: a column of expanded panels down a list of twenty answers is how a
  // list stops being readable.
  const [sharing, setSharing] = useState('');
  const [pushing, setPushing] = useState('');
  // Which answer has its verification panel open.
  const [verifying, setVerifying] = useState('');
  const [items, setItems] = useState(null);
  // Which answers go into a report, by record name - the sidecar is what the
  // builder needs, so an answer with no record cannot be selected.
  const [picked, setPicked] = useState(() => new Set());
  const [reportTitle, setReportTitle] = useState('');
  const [building, setBuilding] = useState('');
  const [error, setError] = useState('');
  // Which one is open, and what it contains. Fetched on demand rather than
  // with the list: a project with fifty kept answers should not pull fifty
  // records to draw a list of titles.
  const [openName, setOpenName] = useState('');
  const [record, setRecord] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const data = await fetchSavedAnswers(projectId);
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(errorMessage(e));
      setItems([]);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // Re-reads the list so a sign-off shows as a badge without a page reload.
  // The verify route stamps the file, so refetching is the whole of it - no
  // second copy of the state to keep in step.
  const reloadRows = load;

  const view = useCallback(async (item) => {
    if (openName === item.blobName) { setOpenName(''); setRecord(null); return; }
    setOpenName(item.blobName);
    setRecord(null);
    setError('');
    try {
      setRecord(await fetchSavedAnswerRecord(item.recordName));
    } catch (e) {
      setError(errorMessage(e));
      setOpenName('');
    }
  }, [openName]);

  // Word is built on demand from the stored answer rather than kept as a
  // second file. The PDF is the record; Word is a convenience, and producing
  // it from the same text means the two cannot disagree.
  const downloadWord = useCallback(async (item) => {
    setBusy(item.blobName);
    setError('');
    try {
      const saved = record && openName === item.blobName ? record : await fetchSavedAnswerRecord(item.recordName);
      await exportChatAnswer({
        format: 'docx',
        title: saved.title,
        question: saved.question,
        blocks: formatAnswer(saved.text || ''),
        sources: saved.sources || [],
        projectName: saved.projectName || '',
      });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy('');
    }
  }, [record, openName]);

  function togglePick(item) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(item.recordName)) next.delete(item.recordName);
      else next.add(item.recordName);
      return next;
    });
  }

  // Build in the order they were kept, oldest first, which is the order the
  // questions were asked and the order a report reads in. Selection order
  // would make the same choice produce a different document twice.
  const build = useCallback(async (format) => {
    const chosen = (items || []).filter((i) => picked.has(i.recordName));
    if (!chosen.length) return;
    setBuilding(format);
    setError('');
    try {
      const ordered = [...chosen].sort((a, b) => str(a.savedAt).localeCompare(str(b.savedAt)));
      const records = await Promise.all(ordered.map((i) => fetchSavedAnswerRecord(i.recordName)));
      const sections = records.map((r) => ({
        title: r.title || '',
        question: r.question || '',
        text: r.text || '',
        blocks: formatAnswer(r.text || ''),
        sources: r.sources || [],
        // The model that produced THIS answer. A report's own model wrote only
        // its executive summary; without this, a signed report could name that
        // one model and say nothing about what produced the answers it is
        // actually made of.
        model: r.model || '',
      }));
      const built = await buildSavedReport({
        projectId,
        projectName,
        title: reportTitle,
        format,
        sections,
      });
      if (built?.blobName) {
        await downloadBlob(built.blobName, `${built.title}.${format}`);
        // Not a failure, but the reader should know the summary is missing
        // rather than wonder why the report opens on the questions.
        if (built.summarised === false) setError('The report was built, but the executive summary could not be generated.');
      }
      setPicked(new Set());
      setReportTitle('');
      onBuilt?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBuilding('');
    }
  }, [items, picked, projectId, projectName, reportTitle, onBuilt]);

  // Nothing kept yet is not worth a card - it would read as a feature that is
  // broken rather than one not used.
  if (items !== null && !items.length && !error) return null;

  return (
    <Card title="Saved answers" hint="Chat answers kept as a record. Not indexed, so they are never used as a source for later answers.">
      <ErrorText>{error}</ErrorText>
      {items === null ? <Loading text="Looking…" /> : null}
      {(items || []).map((item) => (
        <div key={item.blobName} className="saved-answer">
          <div className="procore-option">
            {item.recordName ? (
              <input
                type="checkbox"
                checked={picked.has(item.recordName)}
                onChange={() => togglePick(item)}
                aria-label={`Include ${item.title} in a report`}
              />
            ) : null}
            <span className="procore-option-name">
              {item.title}
              <VerifiedBadge verifiedBy={item.verifiedBy} verifiedAt={item.verifiedAt} />
              <ProcoreSentBadge procore={item.procore} />
            </span>
            <span className="procore-option-code">
              {item.savedAt ? new Date(item.savedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
              {item.savedBy ? ` · ${item.savedBy}` : ''}
            </span>
          </div>
          <div className="row" style={{ gap: 8, marginBottom: 10 }}>
            {/* An answer kept before the record existed has a file and nothing
                to read, so it offers the PDF alone rather than a View that
                would fail. */}
            {item.recordName ? (
              <Button compact secondary onClick={() => view(item)}>
                {openName === item.blobName ? 'Hide' : 'View'}
              </Button>
            ) : null}
            <Button compact secondary onClick={() => downloadBlob(item.blobName, `${item.title}.pdf`)}>
              Download PDF
            </Button>
            {item.recordName ? (
              <Button compact secondary disabled={busy === item.blobName} onClick={() => downloadWord(item)}>
                {busy === item.blobName ? 'Preparing…' : 'Download Word'}
              </Button>
            ) : null}
            {/* Out to people, and out to the project record. Kept next to the
                downloads because they are the same decision - who needs this -
                answered three different ways. */}
            {/* Before Email and Procore, because signing is what makes
                sending it worth doing - and the signed copy is what those two
                then send. */}
            <Button
              compact
              secondary
              onClick={() => { setVerifying(verifying === item.blobName ? '' : item.blobName); setSharing(''); setPushing(''); }}
            >
              {verifying === item.blobName ? 'Close' : item.verifiedBy ? 'Verification' : 'Verify'}
            </Button>
            <Button
              compact
              secondary
              onClick={() => { setSharing(sharing === item.blobName ? '' : item.blobName); setPushing(''); setVerifying(''); }}
            >
              {sharing === item.blobName ? 'Close' : 'Email'}
            </Button>
            {project?.procoreProjectId ? (
              <Button
                compact
                secondary
                onClick={() => { setPushing(pushing === item.blobName ? '' : item.blobName); setSharing(''); setVerifying(''); }}
              >
                {pushing === item.blobName ? 'Close' : 'Send to Procore'}
              </Button>
            ) : null}
          </div>
          {verifying === item.blobName ? (
            <VerifySavedFile blobName={item.blobName} title={item.title} onVerified={reloadRows} />
          ) : null}
          {sharing === item.blobName ? (
            <ShareSavedFile blobName={item.blobName} title={item.title} />
          ) : null}
          {pushing === item.blobName ? (
            <ProcoreSendToDocumentsCard
              // The stored file's own name, because an answer kept as Word
              // would otherwise be pushed to Procore labelled .pdf.
              doc={{ blobName: item.blobName, fileName: item.blobName.split('/').pop() }}
              project={project}
              onSent={load}
            />
          ) : null}
          {openName === item.blobName ? (
            <div className="saved-answer-body">
              {!record ? <Loading text="Opening…" /> : (
                <>
                  {record.question ? <p className="card-hint"><strong>Question:</strong> {record.question}</p> : null}
                  {/* The same component Chat renders with, from the same text.
                      Citation jumps are inert here - the source cards they
                      point at are not on this page. */}
                  <AnswerBody text={record.text || ''} onJump={() => {}} />
                </>
              )}
            </div>
          ) : null}
        </div>
      ))}

      {picked.size ? (
        <div className="saved-answer-build">
          <p className="card-hint" style={{ marginTop: 0 }}>
            {picked.size} answer{picked.size === 1 ? '' : 's'} selected. The report opens with a summary,
            the questions covered and an executive summary written from the answers, then reproduces each
            answer in full with its sources.
          </p>
          <input
            className="input"
            value={reportTitle}
            onChange={(e) => setReportTitle(e.target.value)}
            placeholder={`${projectName || 'Project'} report`}
          />
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <Button disabled={!!building} onClick={() => build('pdf')}>
              {building === 'pdf' ? 'Building…' : 'Build PDF'}
            </Button>
            <Button secondary disabled={!!building} onClick={() => build('docx')}>
              {building === 'docx' ? 'Building…' : 'Build Word'}
            </Button>
            <Button secondary disabled={!!building} onClick={() => setPicked(new Set())}>Clear</Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

// Reports built from kept answers. Separate from the answers themselves so
// that building a report cannot sweep up an earlier report - a summary of
// summaries is how a document stops meaning anything.
function BuiltReports({ projectId, project, refreshKey }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [sharing, setSharing] = useState('');
  const [pushing, setPushing] = useState('');
  const [verifying, setVerifying] = useState('');
  // Bumped after a push or a sign-off, so the badge appears without the page
  // being reloaded. Separate from refreshKey, which the parent owns and uses
  // to announce that a NEW report was built.
  const [reloadKey, setReloadKey] = useState(0);

  const reloadRows = () => setReloadKey((n) => n + 1);

  useEffect(() => {
    let live = true;
    fetchSavedReports(projectId)
      .then((data) => { if (live) setItems(Array.isArray(data?.items) ? data.items : []); })
      .catch((e) => { if (live) { setError(errorMessage(e)); setItems([]); } });
    return () => { live = false; };
  }, [projectId, refreshKey, reloadKey]);

  if (items !== null && !items.length && !error) return null;

  return (
    <Card title="Reports" hint="Built from kept answers. Stored with the project, and not indexed.">
      <ErrorText>{error}</ErrorText>
      {items === null ? <Loading text="Looking…" /> : null}
      {(items || []).map((item) => (
        <div key={item.blobName} className="saved-answer">
          <div className="procore-option">
            <span className="procore-option-name">
              {item.title}
              <VerifiedBadge verifiedBy={item.verifiedBy} verifiedAt={item.verifiedAt} />
              <ProcoreSentBadge procore={item.procore} />
            </span>
            <span className="procore-option-code">
              {item.answerCount ? `${item.answerCount} answer${item.answerCount === 1 ? '' : 's'}` : ''}
              {item.savedAt ? ` · ${new Date(item.savedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
            </span>
          </div>
          <div className="row" style={{ gap: 8, marginBottom: 10 }}>
            <Button compact secondary onClick={() => downloadBlob(item.blobName, item.title)}>
              Download
            </Button>
            {/* Before Email and Procore, because signing is what makes
                sending it worth doing - and the signed copy is what those two
                then send. */}
            <Button
              compact
              secondary
              onClick={() => { setVerifying(verifying === item.blobName ? '' : item.blobName); setSharing(''); setPushing(''); }}
            >
              {verifying === item.blobName ? 'Close' : item.verifiedBy ? 'Verification' : 'Verify'}
            </Button>
            <Button
              compact
              secondary
              onClick={() => { setSharing(sharing === item.blobName ? '' : item.blobName); setPushing(''); setVerifying(''); }}
            >
              {sharing === item.blobName ? 'Close' : 'Email'}
            </Button>
            {project?.procoreProjectId ? (
              <Button
                compact
                secondary
                onClick={() => { setPushing(pushing === item.blobName ? '' : item.blobName); setSharing(''); setVerifying(''); }}
              >
                {pushing === item.blobName ? 'Close' : 'Send to Procore'}
              </Button>
            ) : null}
          </div>
          {verifying === item.blobName ? (
            <VerifySavedFile blobName={item.blobName} title={item.title} onVerified={reloadRows} />
          ) : null}
          {sharing === item.blobName ? (
            <ShareSavedFile blobName={item.blobName} title={item.title} />
          ) : null}
          {pushing === item.blobName ? (
            <ProcoreSendToDocumentsCard
              // The stored name's own extension, not the title's - a report
              // built as Word must not arrive in Procore called .pdf.
              doc={{ blobName: item.blobName, fileName: item.blobName.split('/').pop() }}
              project={project}
              onSent={() => setReloadKey((n) => n + 1)}
            />
          ) : null}
        </div>
      ))}
    </Card>
  );
}

export default function ReportsPage() {
  const { project } = useProject();
  const [reportKind, setReportKind] = useState('project_report');
  // Both drawing reports are produced as either a PDF or a spreadsheet by the
  // same backend branch, so the choice is one control shared by the two.
  const [drawingFormat, setDrawingFormat] = useState('pdf');
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [ready, setReady] = useState(null);

  const projectName = getProjectName(project);
  // Bumped after a build so the Reports card re-reads without a page reload.
  const [builtKey, setBuiltKey] = useState(0);

  // Every flow is request -> poll -> hand back a file, so they share one
  // wrapper rather than repeating the same status/error bookkeeping. Written
  // as straight-line awaits: the previous setInterval version had to be told
  // which status endpoint to poll before it knew, and captured stale state.
  async function run(kind, work) {
    setBusy(kind);
    setError('');
    setReady(null);
    try {
      const { sasUrl, fileName, fallbackName } = await work();
      if (!sasUrl) {
        setError('The report was built, but no download link came back. That happens when the storage connection string is not account-key based, so a SAS URL cannot be minted.');
        return;
      }
      const name = fileName || fallbackName;
      setReady({ url: sasUrl, fileName: name });
      startDownload(sasUrl, name);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setStatus('');
      setBusy('');
    }
  }

  function handleDocuments() {
    return run('documents', async () => {
      setStatus('Queuing documents package…');
      const { jobId } = await requestReportDownload({ projectName });
      setStatus('Building your documents package…');
      const result = await waitForReport(fetchReportDownloadStatus, jobId);
      if (result.status !== 'complete') throw new Error(result.error || 'Report failed to build.');
      return { ...result, fallbackName: 'documents.zip' };
    });
  }

  function handlePdf() {
    return run('pdf', async () => {
      setStatus('Queuing report…');
      const { jobId } = await requestPdfReportDownload({
        projectId: getProjectId(project),
        projectName,
        reportKind,
        type: reportKind,
      });
      setStatus('Generating your report…');
      const result = await waitForReport(fetchPdfReportDownloadStatus, jobId);
      if (result.status !== 'complete') throw new Error(result.error || 'Report failed to build.');
      return { ...result, fallbackName: 'report.pdf' };
    });
  }

  // The drawing register and the comparison report are built by the documents
  // endpoint, not the PDF one - runDownloadReportJob branches on flags in the
  // body rather than exposing separate routes - so both poll the documents
  // status endpoint.
  function handleDrawingRegister() {
    return run('register', async () => {
      setStatus('Building the drawing register…');
      const { jobId } = await requestDrawingRegister({ projectId: getProjectId(project), projectName, format: drawingFormat });
      const result = await waitForReport(fetchReportDownloadStatus, jobId);
      if (result.status !== 'complete') throw new Error(result.error || 'Drawing register failed to build.');
      return { ...result, fallbackName: `drawing-register.${drawingFormat === 'excel' ? 'xlsx' : 'pdf'}` };
    });
  }

  function handleDrawingComparison() {
    return run('comparison', async () => {
      setStatus('Building the drawing comparison report…');
      const { jobId } = await requestDrawingComparison({ projectId: getProjectId(project), projectName, format: drawingFormat });
      const result = await waitForReport(fetchReportDownloadStatus, jobId);
      if (result.status !== 'complete') throw new Error(result.error || 'Drawing comparison report failed to build.');
      return { ...result, fallbackName: `drawing-comparison.${drawingFormat === 'excel' ? 'xlsx' : 'pdf'}` };
    });
  }

  return (
    <>
      <h1>Reports</h1>
      <p className="sub">{projectName}</p>
      <ErrorText>{error}</ErrorText>

      {ready && (
        <div className="card">
          <p className="saved" style={{ margin: '0 0 12px' }}>Your report is ready.</p>
          <a className="btn" href={ready.url} download={ready.fileName} rel="noopener">
            Download {ready.fileName}
          </a>
          <p className="card-hint" style={{ margin: '10px 0 0' }}>
            The download should have started on its own. If your browser blocked it, use the button above — the link stays valid for an hour.
          </p>
        </div>
      )}

      <BuiltReports projectId={getProjectId(project)} project={project} refreshKey={builtKey} />

      <SavedAnswers
        projectId={getProjectId(project)}
        projectName={projectName}
        project={project}
        onBuilt={() => setBuiltKey((n) => n + 1)}
      />

      <Card title="PDF report">
        <PillGroup items={PDF_REPORT_KINDS} value={reportKind} onChange={setReportKind} />
        <Button onClick={handlePdf} disabled={!!busy} style={{ marginTop: 12 }}>
          {busy === 'pdf' ? 'Working…' : 'Generate and download'}
        </Button>
      </Card>

      <Card title="Drawings" hint="The register lists every drawing and its revision history. The comparison report covers revisions that were checked against a previous version — a first upload with nothing to compare against appears in the register instead.">
        <PillGroup
          items={[{ id: 'pdf', label: 'PDF' }, { id: 'excel', label: 'Excel' }]}
          value={drawingFormat}
          onChange={setDrawingFormat}
        />
        <div className="row" style={{ marginTop: 12 }}>
          <Button onClick={handleDrawingRegister} disabled={!!busy}>
            {busy === 'register' ? 'Working…' : 'Drawing register'}
          </Button>
          <Button secondary onClick={handleDrawingComparison} disabled={!!busy}>
            {busy === 'comparison' ? 'Working…' : 'Drawing comparison'}
          </Button>
        </div>
      </Card>

      <Card title="Documents package" hint="Every uploaded file for this project, zipped.">
        <Button secondary onClick={handleDocuments} disabled={!!busy}>
          {busy === 'documents' ? 'Working…' : 'Download documents (.zip)'}
        </Button>
      </Card>

      {status && <Loading text={status} />}
    </>
  );
}
